import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Landmark, Wallet, QrCode, ArrowDownLeft, ArrowUpRight, Loader2, RefreshCw, CheckCircle2, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { coinToRp, fmtDate, TX_LABEL, METHOD_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLive } from "@/context/Realtime";
import CoinCard from "@/components/CoinCard";
import { Empty, PageHead } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const METHODS = [
  ["bank_transfer", "Bank Transfer", Landmark, ["BCA", "BNI", "BRI", "MANDIRI", "PERMATA", "CIMB", "BSI"]],
  ["ewallet", "E-Wallet", Wallet, []],
  ["qris", "QRIS", QrCode, []],
];
const PRESETS = [10, 25, 50, 100, 250, 500];

function PaymentInfo({ t, onCheck }) {
  const p = t.payment || {};
  useEffect(() => {
    if (t.status !== "pending" || t.gateway !== "komerce") return undefined;
    const iv = setInterval(() => onCheck(true), 5000);
    return () => clearInterval(iv);
  }, [t.status, t.gateway]); // eslint-disable-line react-hooks/exhaustive-deps
  if (t.status === "approved") {
    return (
      <div data-testid="topup-success" className="text-center space-y-2 py-6">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
        <p className="font-display text-xl font-extrabold">Pembayaran berhasil</p>
        <p className="text-sm text-slate-500">{t.coins} koin telah ditambahkan ke saldo Anda.</p>
      </div>
    );
  }
  if (t.status === "rejected") return <p data-testid="topup-expired" className="text-center text-sm text-red-600 py-6">Pembayaran kedaluwarsa atau dibatalkan. Silakan buat top up baru.</p>;
  return (
    <div data-testid="topup-payment-info" className="space-y-3 text-center">
      <p className="text-sm text-slate-500">Bayar <b className="text-slate-900">{coinToRp(t.coins)}</b> untuk {t.coins} koin</p>
      {p.qr_string && <div className="grid place-items-center"><QRCodeSVG data-testid="qris-code" value={p.qr_string} size={200} /></div>}
      {p.qr_string && t.method === "ewallet" && <p className="text-xs text-slate-500">Scan QRIS ini dengan aplikasi e-wallet Anda (GoPay, OVO, DANA, ShopeePay).</p>}
      {p.qr_string && t.gateway === "komerce" && <p className="text-[11px] text-slate-400">QRIS berlaku 5 menit</p>}
      {p.va_number && <div className="bg-slate-50 rounded-2xl p-4"><p className="text-xs text-slate-500">Virtual Account {p.bank_name || p.bank}</p><p data-testid="va-number" className="font-mono text-xl font-bold">{p.va_number}</p></div>}
      {t.method === "ewallet" && !p.qr_string && <div className="bg-slate-50 rounded-2xl p-4"><p className="text-xs text-slate-500">Transfer ke {p.provider}</p><p className="font-mono text-xl font-bold">{p.account}</p></div>}
      {p.payment_url && <a data-testid="payment-url-link" href={p.payment_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700 underline"><ExternalLink className="w-4 h-4" />Buka halaman pembayaran</a>}
      {t.gateway === "mock" && <p className="text-[11px] text-amber-800 bg-amber-50 rounded-xl px-3 py-2">Mode simulasi: pembayaran akan diverifikasi & disetujui admin.</p>}
      {t.gateway === "komerce" && <p className="text-[11px] text-slate-500 flex items-center justify-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Menunggu pembayaran, status dicek otomatis</p>}
      <p className="text-xs font-mono text-slate-400">ID: {t.id}</p>
      <button data-testid="check-topup-status-btn" onClick={() => onCheck(false)} className="h-10 px-5 rounded-full border text-sm font-bold inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" />Cek Status</button>
    </div>
  );
}

function TopupDialog({ open, onOpenChange, onDone, initialMethod }) {
  const { user } = useAuth();
  const [coins, setCoins] = useState(50);
  const [method, setMethod] = useState("qris");
  const [channel, setChannel] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!open) setResult(null);
    else { setMethod(initialMethod || "qris"); setChannel(""); }
  }, [open, initialMethod]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/topups", { coins: Number(coins), method, channel: channel || null });
      setResult(data);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const check = async (silent) => {
    try {
      const { data } = await api.post(`/topups/${result.id}/check`);
      setResult(data);
      if (data.status === "approved") { toast.success("Pembayaran berhasil, koin ditambahkan"); onDone(); }
      else if (!silent) toast.info("Pembayaran masih menunggu");
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
    }
  };
  useLive((e) => {
    if (result && result.status === "pending" && (e.type === "wallet" || e.type === "notification")) check(true);
  });
  const m = METHODS.find((x) => x[0] === method);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Top Up Koin</DialogTitle></DialogHeader>
        {result ? <PaymentInfo t={result} onCheck={check} /> : (
          <div className="space-y-4" data-testid="topup-form">
            <div className="grid grid-cols-3 gap-2">
              {PRESETS.map((p) => <button key={p} data-testid={`topup-preset-${p}`} onClick={() => setCoins(p)} className={`h-12 rounded-xl border-2 font-mono font-bold text-sm ${Number(coins) === p ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200"}`}>{p}</button>)}
            </div>
            <label className="block"><span className="text-xs font-semibold text-slate-600">Jumlah koin (min. 10)</span>
              <input data-testid="topup-amount-input" type="number" min="10" className="field mt-1 font-mono" value={coins} onChange={(e) => setCoins(e.target.value)} />
              <span className="text-xs text-slate-500">Total bayar: {coinToRp(Number(coins) || 0)}</span></label>
            <div data-testid="payment-method-select" className="space-y-2">
              {METHODS.map(([k, l, I]) => (
                <button key={k} data-testid={`payment-method-${k}`} onClick={() => { setMethod(k); setChannel(""); }}
                  className={`w-full h-12 px-4 rounded-xl border-2 flex items-center gap-3 font-bold text-sm ${method === k ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}><I className="w-5 h-5" />{l}</button>
              ))}
            </div>
            {m[3].length > 0 && (
              <select data-testid="payment-channel-select" className="field" value={channel} onChange={(e) => setChannel(e.target.value)}>
                <option value="">Pilih {m[1]}</option>{m[3].map((c) => <option key={c}>{c}</option>)}
              </select>
            )}
            <button data-testid="confirm-topup-btn" disabled={busy || Number(coins) < 10} onClick={submit}
              className="w-full h-12 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Lanjutkan Pembayaran
            </button>
            <p className="text-[11px] text-slate-400 text-center">Saldo saat ini: {user.coins} koin</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TOPUP_STATUS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" };
const TOPUP_LABEL = { pending: "Menunggu", approved: "Berhasil", rejected: "Ditolak" };

export default function CoinPage() {
  const { user, mode, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("topup") === "1");
  const [initMethod, setInitMethod] = useState("qris");
  const [tab, setTab] = useState("tx");
  useEffect(() => {
    if (params.get("topup") === "1") setOpen(true);
  }, [params]);
  const tx = useFetch("/coins/transactions", { live: (e) => e.type === "wallet" });
  const tops = useFetch("/topups/mine", { live: (e) => e.type === "wallet" || (e.type === "notification" && e.notification?.type === "topup") });
  const reload = () => { tx.reload(); tops.reload(); refreshUser(); };
  const onOpen = (v) => { setOpen(v); if (!v) { setParams({}); reload(); } };
  return (
    <div data-testid="coin-wallet-page" className="space-y-5">
      <PageHead title="Dompet Koin" sub="1 koin = Rp1.000" />
      <CoinCard user={user} worker={mode === "worker"} />
      <div className="grid grid-cols-3 gap-2 text-center">
        {METHODS.map(([k, l, I]) => <button key={k} data-testid={`quick-topup-${k}`} onClick={() => { setInitMethod(k); setOpen(true); }} className="bg-white rounded-2xl border p-3 hover:shadow-md transition-shadow"><I className="w-5 h-5 mx-auto text-emerald-600" /><p className="text-[11px] font-bold mt-1">{l}</p></button>)}
      </div>
      <div className="inline-flex p-1 bg-slate-100 rounded-full">
        {[["tx", "Riwayat Koin"], ["topup", "Riwayat Top Up"]].map(([k, l]) => (
          <button key={k} data-testid={`coin-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-sm font-bold ${tab === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {tab === "tx" ? (
        <div data-testid="transaction-history-list" className="bg-white rounded-2xl border divide-y">
          {!tx.data?.length ? <div className="p-4"><Empty title="Belum ada transaksi" /></div> : tx.data.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3">
              <span className={`w-9 h-9 rounded-full grid place-items-center ${t.amount > 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>{t.amount > 0 ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}</span>
              <div className="flex-1 min-w-0"><p className="text-sm font-bold">{TX_LABEL[t.type] || t.type}</p><p className="text-xs text-slate-500 truncate">{t.description}</p><p className="text-[11px] text-slate-400">{fmtDate(t.created_at)}</p></div>
              <span className={`font-mono font-bold text-sm ${t.amount > 0 ? "text-emerald-600" : "text-red-500"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</span>
            </div>
          ))}
        </div>
      ) : (
        <div data-testid="topup-history-list" className="bg-white rounded-2xl border divide-y">
          {!tops.data?.length ? <div className="p-4"><Empty title="Belum ada top up" /></div> : tops.data.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3">
              <div className="flex-1"><p className="text-sm font-bold">{t.coins} koin · {METHOD_LABEL[t.method]}</p><p className="text-[11px] text-slate-400">{t.id} · {fmtDate(t.created_at)}</p></div>
              <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${TOPUP_STATUS[t.status]}`}>{TOPUP_LABEL[t.status]}</span>
            </div>
          ))}
        </div>
      )}
      <TopupDialog open={open} onOpenChange={onOpen} onDone={reload} initialMethod={initMethod} />
    </div>
  );
}
